import { Amplify } from 'aws-amplify';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { getResourceName } from '../env/EnvUtils';
import { createLogger } from './logger';

export class Api {
  private readonly apiEndpoint: string;
  private readonly logger: ReturnType<typeof createLogger>;

  constructor(apiResourceName: string, pathSuffix: string = '', loggerName = 'Api') {
    this.logger = createLogger(loggerName);
    const config = Amplify.getConfig();
    const apiConfig = config.API?.REST?.[getResourceName(apiResourceName)];
    if (!apiConfig?.endpoint) {
      this.logger.error(`${apiResourceName} API endpoint not configured`);
      this.apiEndpoint = '';
      return;
    }
    // Remove trailing slash and append pathSuffix if provided
    this.apiEndpoint = `${apiConfig.endpoint.replace(/\/$/, '')}${pathSuffix}`;
    this.logger.debug('API endpoint set', { endpoint: this.apiEndpoint });
  }

  // Check if user is authenticated
  private async isAuthenticated(): Promise<boolean> {
    this.logger.debug('Checking if user is authenticated');

    try {
      const currentUser = await getCurrentUser();
      this.logger.debug('Current user', { currentUser });

      return !!currentUser;
    } catch (error) {
      this.logger.error('Error checking if user is authenticated', { error });
      return false;
    }
  }

  // Get Cognito JWT token for authenticated requests
  private async getAuthToken(): Promise<string | null> {
    try {
      // Check if user is authenticated
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        this.logger.debug('No authenticated user found');
        return null;
      }
      // Get the current session with tokens
      const session = await fetchAuthSession();

      // Get the ID token (JWT) for API authorization
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        this.logger.debug('No ID token found in session');
        return null;
      }

      this.logger.debug('Successfully retrieved auth token');
      return idToken;
    } catch (error) {
      this.logger.debug('Failed to get auth token', { error });
      return null;
    }
  }

  /**
   * Make a request with optional authentication
   * If user is authenticated, adds auth token. If not, make public request.
   * @param method - HTTP method (GET, POST, PUT, DELETE)
   * @param path - API endpoint path
   * @param body - Request body (optional)
   * @param headers - Additional headers (optional)
   * @returns Response data
   */
  async optionalAuthRequest<T = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string = '',
    body?: unknown,
    additionalHeaders: Record<string, string> = {}
  ): Promise<T> {
    const isAuthenticated = await this.isAuthenticated();

    this.logger.debug('User is authenticated', { isAuthenticated });

    if (!isAuthenticated) {
      return this.unauthenticatedRequest<T>(method, path, body, additionalHeaders);
    }

    return this.authenticatedRequest<T>(method, path, body, additionalHeaders);
  }

  /**
   * Make an unauthenticated API request
   * @param method - HTTP method (GET, POST, PUT, DELETE)
   * @param path - API endpoint path
   * @param body - Request body (optional)
   * @param additionalHeaders - Additional headers (optional)
   * @returns Response data
   */
  async unauthenticatedRequest<T = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string = '',
    body?: unknown,
    additionalHeaders: Record<string, string> = {}
  ): Promise<T> {
    const headers = {
      'Content-Type': 'application/json',
      ...additionalHeaders,
      // Explicitly do NOT include Authorization header
    };

    this.logger.debug('Making unauthenticated API request');
    return this.request<T>(method, path, body, headers);
  }

  /**
   * Make an authenticated API request (automatically adds Cognito JWT)
   * @param method - HTTP method (GET, POST, PUT, DELETE)
   * @param path - API endpoint path
   * @param body - Request body (optional)
   * @param additionalHeaders - Additional headers (optional)
   * @returns Response data
   */
  async authenticatedRequest<T = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string = '',
    body?: unknown,
    additionalHeaders: Record<string, string> = {}
  ): Promise<T> {
    const authToken = await this.getAuthToken();

    if (!authToken) {
      this.logger.error('No auth token found.');
      throw new Error('Authentication required. Please log in.');
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
      ...additionalHeaders,
    };

    this.logger.debug('Making authenticated API request');
    return this.request<T>(method, path, body, headers);
  }

  /**
   * Make a request to the API
   * @param method - HTTP method (GET, POST, PUT, DELETE)
   * @param path - API endpoint path
   * @param body - Request body (optional)
   * @param headers - Additional headers (optional)
   * @returns Response data
   */
  async request<T = unknown>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string = '',
    body?: unknown,
    headers: Record<string, string> = { 'Content-Type': 'application/json' },
    includeCredentials: boolean = false
  ): Promise<T> {
    if (!this.apiEndpoint) {
      this.logger.error('API endpoint not set');
      throw new Error('API endpoint not set');
    }

    const url = `${this.apiEndpoint}${path}`;

    this.logger.debug('Making API request', {
      method,
      url,
      hasBody: !!body,
      headers: Object.keys(headers),
      hasAuth: !!headers.Authorization,
      includeCredentials,
    });

    try {
      const response = await fetch(url, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        credentials: includeCredentials ? 'include' : 'omit',
        mode: 'cors',
        cache: 'no-cache',
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.logger.error('API call failed', {
          status: response.status,
          statusText: response.statusText,
          errorText,
        });

        // Handle different error types
        if (response.status === 401) {
          throw new Error('Authentication failed. Please log in.');
        } else if (response.status === 403) {
          throw new Error('Access denied. You do not have permission for this action.');
        } else {
          throw new Error(`API call failed: ${response.status} ${response.statusText}`);
        }
      }

      const responseData = await response.json();
      this.logger.debug('API request successful', { status: response.status });

      return responseData as T;
    } catch (error) {
      this.logger.error('API request error', { error });
      throw error;
    }
  }
}
